import React, { useState } from "react";
import {
  ShieldCheck,
  Globe,
  Mail,
  ExternalLink,
  Package,
  Lock,
  Eye,
} from "lucide-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import ConfirmDeleteModal from "./ConfirmDeleteModal ";
import { useDispatch } from "react-redux";
import EditAgentModal from "./EditAgentModal";
import { deleteAgent } from "../../../RTK/agents/DeleteAgent";
import { fetchAgentDetails, fetchAgents } from "../../../RTK/agents/GetAgents";
import AgentProductsPopup from "./AgentProductsPopup";
import { usePermissions } from "../../../contexts/PermissionsContext";

const AgentDetailsModal = ({ agentDetails, onClose }) => {
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions();

  // ✅ فحص الصلاحية
  const canManageAgents = hasPermission("manage_agents");

  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [showProductsPopup, setShowProductsPopup] = useState(false);

  if (!agentDetails) return null;

  const StatusBadge = ({ isActive }) => (
    <div
      className={`px-2 py-1 text-sm rounded-full ${
        isActive
          ? "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400"
          : "bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400"
      }`}
    >
      {isActive ? "Active" : "Inactive"}
    </div>
  );

  const StatBox = ({ label, value }) => (
    <div className="bg-white/5 rounded-lg p-4 hover:bg-white/10 transition-colors flex items-center justify-between">
      <p className="text-gray-500 dark:text-gray-400 text-sm mb-1">{label}</p>
      <p className="text-xl font-bold">{value}</p>
    </div>
  );

  const DetailRow = ({
    icon: Icon,
    label,
    value,
    isCopyable = false,
    onCopy,
    onExternalLink,
  }) => (
    <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors">
      <div className="flex items-center gap-3">
        <Icon className="w-4 h-4 text-gray-400" />
        <span className="text-sm">{label}</span>
      </div>
      <div className="text-sm font-medium flex items-center gap-2">
        <span className="max-w-[130px] sm:max-w-[250px] lg:max-w-[300px] truncate overflow-hidden text-ellipsis">
          {value}
        </span>
        {onExternalLink && (
          <button
            onClick={onExternalLink}
            className="text-primary hover:underline"
          >
            <ExternalLink className="w-4 h-4" />
          </button>
        )}
        {isCopyable && (
          <button
            onClick={onCopy}
            className="text-gray-400 hover:text-gray-600"
          >
            <ContentPasteIcon className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );

  const handleCopy = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success(`Copied: ${text}`, { autoClose: 2000 }))
      .catch(() =>
        toast.error("Failed to copy! Please try again.", { autoClose: 2000 }),
      );
  };

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleDelete = async () => {
    if (!canManageAgents) {
      toast.error("ليس لديك صلاحية لحذف الوكلاء 🔒");
      return;
    }

    try {
      await dispatch(deleteAgent(agentDetails.id)).unwrap();
      await dispatch(fetchAgents()).unwrap();
      toast.success("Agent deleted successfully!");

      setShowConfirmDelete(false);
      setShowEditModal(false);
      onClose();
      handleClose();
    } catch (error) {
      toast.error(error || "Failed to delete agent");
      setShowConfirmDelete(false);
    }
  };

  const handleDeleteClick = () => {
    if (!canManageAgents) {
      toast.error("ليس لديك صلاحية لحذف الوكلاء 🔒");
      return;
    }
    setShowConfirmDelete(true);
  };

  const handleEditClick = () => {
    if (!canManageAgents) {
      toast.error("ليس لديك صلاحية لتعديل الوكلاء 🔒");
      return;
    }
    setShowEditModal(true);
  };

  const handleOpenProducts = () => {
    if (!canManageAgents) {
      toast.error("ليس لديك صلاحية لعرض منتجات الوكلاء 🔒");
      return;
    }
    console.log("🔵 Opening products popup for agent:", agentDetails.id);
    setShowProductsPopup(true);
  };

  const handleCloseProducts = () => {
    console.log("🔴 Closing products popup");
    setShowProductsPopup(false);
  };

  return (
    <>
      <div className="fixed inset-0 flex items-center justify-center z-[5000] bg-black/90 backdrop-blur">
        <div className="bg-white/10 backdrop-blur-lg rounded-xl z-[5100] overflow-hidden border border-white/10 w-[90%] max-w-lg">
          {/* Header */}
          <div className="p-6 border-b border-white/10 relative">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <img
                  src={agentDetails.icon || "avatar.png"}
                  alt={agentDetails.name}
                  className={`w-12 h-12 rounded-full object-cover border-4 ${
                    agentDetails.is_active
                      ? "border-green-900"
                      : "border-yellow-900"
                  }`}
                />
                <div>
                  <h3 className="text-lg font-bold mb-1">
                    {agentDetails.name}
                  </h3>
                  <StatusBadge isActive={agentDetails.is_active} />
                </div>
              </div>

              {/* ✅ شارة "عرض فقط" */}
              {!canManageAgents && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-800/80 border border-slate-700 rounded-lg">
                  <Eye className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-300 text-xs font-medium">
                    عرض فقط
                  </span>
                </div>
              )}
            </div>

            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* ✅ رسالة تحذيرية لو مافيش صلاحية */}
            {!canManageAgents && (
              <div className="flex items-center gap-2 p-3 bg-orange-900/20 border border-orange-500/30 rounded-lg">
                <Lock className="w-5 h-5 text-orange-400" />
                <span className="text-orange-300 text-sm">
                  ليس لديك صلاحية لتعديل أو حذف الوكلاء - عرض فقط
                </span>
              </div>
            )}

            <StatBox label="الرصيد" value={`${agentDetails.balance}$`} />

            {/* ✅ زر المنتجات - محمي + Lock */}
            <button
              onClick={handleOpenProducts}
              disabled={!canManageAgents}
              className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-all border ${
                canManageAgents
                  ? "bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border-blue-500/30 hover:border-blue-500/50"
                  : "bg-slate-700/50 text-slate-400 cursor-not-allowed border-slate-600"
              }`}
              title={
                !canManageAgents
                  ? "ليس لديك صلاحية لعرض منتجات الوكلاء"
                  : "عرض منتجات الوكيل"
              }
            >
              {!canManageAgents ? (
                <Lock className="w-5 h-5" />
              ) : (
                <Package className="w-5 h-5" />
              )}
              <span className="font-medium">عرض منتجات الوكيل</span>
            </button>

            <div className="space-y-3">
              <DetailRow
                icon={Mail}
                label="البريد الإلكتروني"
                value={agentDetails.contact_email}
                isCopyable={true}
                onCopy={() => handleCopy(agentDetails.contact_email)}
                onExternalLink={() =>
                  window.open(`mailto:${agentDetails.contact_email}`, "_self")
                }
              />
              <DetailRow
                icon={Globe}
                label="الموقع الإلكتروني"
                value={agentDetails.website}
                isCopyable={true}
                onCopy={() => handleCopy(agentDetails.website)}
                onExternalLink={() =>
                  window.open(agentDetails.website, "_blank")
                }
              />
              <DetailRow
                icon={ShieldCheck}
                label="API URL"
                value={agentDetails.api_url}
                isCopyable={true}
                onCopy={() => handleCopy(agentDetails.api_url)}
              />
              <DetailRow
                icon={ShieldCheck}
                label="API Token"
                value={agentDetails.api_token}
                isCopyable={true}
                onCopy={() => handleCopy(agentDetails.api_token)}
              />
              <DetailRow
                icon={ShieldCheck}
                label="Channel"
                value={agentDetails.channel}
                isCopyable={true}
                onCopy={() => handleCopy(agentDetails.channel)}
              />
            </div>
          </div>

          {/* ✅ Footer - الأزرار معطلة لو مافيش صلاحية */}
          <div className="flex justify-between gap-4 p-6 border-t border-white/10">
            <button
              onClick={onClose}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              إغلاق
            </button>

            {/* ✅ زر التعديل - معطل + Lock */}
            <button
              onClick={handleEditClick}
              disabled={!canManageAgents}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                canManageAgents
                  ? "bg-blue-600 text-white hover:bg-blue-700"
                  : "bg-slate-700/50 text-slate-400 cursor-not-allowed border border-slate-600"
              }`}
              title={
                !canManageAgents
                  ? "ليس لديك صلاحية لتعديل الوكلاء"
                  : "تعديل الوكيل"
              }
            >
              {!canManageAgents && <Lock className="w-4 h-4" />}
              <span> تعديل</span>
            </button>

            {/* ✅ زر الحذف - معطل + Lock */}
            <button
              onClick={handleDeleteClick}
              disabled={!canManageAgents}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                canManageAgents
                  ? "bg-red-600 text-white hover:bg-red-700"
                  : "bg-slate-700/50 text-slate-400 cursor-not-allowed border border-slate-600"
              }`}
              title={
                !canManageAgents ? "ليس لديك صلاحية لحذف الوكلاء" : "حذف الوكيل"
              }
            >
              {!canManageAgents && <Lock className="w-4 h-4" />}
              <span>حذف</span>
            </button>
          </div>
        </div>

        {/* ✅ Modals - تظهر فقط لو عنده صلاحية */}
        {showConfirmDelete && canManageAgents && (
          <ConfirmDeleteModal
            onConfirm={handleDelete}
            onCancel={() => setShowConfirmDelete(false)}
          />
        )}

        {showEditModal && canManageAgents && (
          <EditAgentModal
            agentDetails={agentDetails}
            onClose={() => setShowEditModal(false)}
            onSave={(updatedData) => {
              setShowEditModal(false);
              onClose();
            }}
          />
        )}
      </div>

      {/* ✅ AgentProductsPopup - يظهر فقط لو عنده صلاحية */}
      {showProductsPopup && canManageAgents && (
        <AgentProductsPopup
          agentId={agentDetails.id}
          agentName={agentDetails.name}
          onClose={handleCloseProducts}
        />
      )}
    </>
  );
};

export default AgentDetailsModal;
